<template>
	<ViewLayout>
		<template #header-title>
			{{ 'Data Subject Grouping Rules' | useLabels }}
		</template>
		<template #content>
			<div class="d-flex flex-row cassie-horizontal-md">
				<v-spacer />
				<PrimaryActionButton
					v-if="userFullPermissions"
					test-id="grouping-rules-add"
					@click="showManageModal = true"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					{{ 'Create' | useLabels }}
				</PrimaryActionButton>
			</div>

			<GroupingRuleList
				:rules="groupingRules"
				:user-full-permissions="userFullPermissions"
				@move-up="moveUp"
				@move-down="moveDown"
				@edit="openEditModal"
				@delete="rule => ruleToBeDeleted = rule"
			/>

			<ManageGroupingRulesModal
				v-if="showManageModal"
				:grouping-rules="groupingRules"
				:grouping-rule-to-edit="groupingRuleToEdit"
				@close="closeManageModal"
			/>
			<ConfirmDeleteModal
				v-if="ruleToBeDeleted"
				:entity-name="ruleToBeDeleted.passName"
				entity-type="Data Subject Grouping Rule"
				@close="ruleToBeDeleted = null"
				@delete="deleteRule"
			/>
		</template>
		<template #footer>
			<PageActionRow v-if="showActionButtons">
				<template #actions>
					<v-spacer />
					<div class="cassie-horizontal-md d-flex">
						<SecondaryActionButton @click="cancelReorder">
							{{ 'Cancel' | useLabels }}
						</SecondaryActionButton>
						<PrimaryActionButton @click="saveReorder">
							{{ 'Save' | useLabels }}
						</PrimaryActionButton>
					</div>
				</template>
			</PageActionRow>
		</template>
	</ViewLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import ManageGroupingRulesModal from './manage-grouping-rules-modal.vue'
import GroupingRuleList from './components/GroupingRuleList.vue'
import { getGroupingRules, upsertGroupingRules, deleteGroupingRule } from '../../../../../../shared/utils/api/data-subject-grouping-rules.js'
import { GROUPING_RULES_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'

export default {
	components: {
		ViewLayout,
		PrimaryActionButton,
		SecondaryActionButton,
		ManageGroupingRulesModal,
		ConfirmDeleteModal,
		PageActionRow,
		GroupingRuleList
	},
	data () {
		return {
			showManageModal: false,
			groupingRuleToEdit: null,
			ruleToBeDeleted: null,
			groupingRules: [],
			showActionButtons: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userFullPermissions () { return this.productAreaPermission(GROUPING_RULES_FULL_PERMISSIONS) }
	},
	created () {
		this.loadGroupingRules()
	},
	methods: {
		async loadGroupingRules () {
			const { data: { groupingRules } } = await getGroupingRules()
			this.groupingRules = groupingRules.sort((a, b) => a.executionOrder - b.executionOrder)
		},
		openEditModal (rule) {
			if (!this.userFullPermissions) return
			this.groupingRuleToEdit = rule
			this.showManageModal = true
		},
		closeManageModal () {
			this.showManageModal = false
			this.groupingRuleToEdit = null
			this.loadGroupingRules()
		},
		moveUp (index) { this.move(index, index - 1) },
		moveDown (index) { this.move(index, index + 1) },
		move (from, to) {
			if (to < 0 || to >= this.groupingRules.length) return
			const copy = [...this.groupingRules]
			const [moved] = copy.splice(from, 1)
			copy.splice(to, 0, moved)
			this.groupingRules = copy
			this.showActionButtons = true
		},
		cancelReorder () {
			this.showActionButtons = false
			this.loadGroupingRules()
		},
		async saveReorder () {
			const renumbered = this.groupingRules.map((rule, idx) => ({ ...rule, executionOrder: idx + 1 }))
			await upsertGroupingRules(renumbered)
			this.showActionButtons = false
			await this.loadGroupingRules()
		},
		async deleteRule () {
			await deleteGroupingRule(this.ruleToBeDeleted.id)
			this.ruleToBeDeleted = null
			await this.loadGroupingRules()
		}
	}
}
</script>
